import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Star, Sparkles } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { CategoryCard } from '../components/CategoryCard';
import { Loading } from '../components/Loading';
import { SunburstRays } from '../components/SunburstRays';
import { FloatingToys } from '../components/FloatingToys';
import { BrandsCarousel } from '../components/BrandsCarousel';
import { getProducts, getCategories, getBanners } from '../utils/firestoreService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_BANNERS = [
  { id: 'default-1', imageUrl: '/fotojugueteria.jpg', alt: 'Diversytoys Principal' },
  { id: 'default-2', imageUrl: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?q=80&w=1000&auto=format&fit=crop', alt: 'Juguetes de Construcción' },
  { id: 'default-3', imageUrl: 'https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?q=80&w=1000&auto=format&fit=crop', alt: 'Diversión para todos' },
];

export const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [randomProducts, setRandomProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const activeBanners = banners.length > 0 ? banners : DEFAULT_BANNERS;
    if (activeBanners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % activeBanners.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [banners]);

  const fetchData = async () => {
    try {
      const [productsData, categoriesData, bannersData] = await Promise.all([
        getProducts(),
        getCategories(),
        getBanners(),
      ]);

      // Productos destacados
      const featured = productsData.filter((p) => p.featured).slice(0, 6);
      setFeaturedProducts(featured);

      // 6 productos aleatorios
      const shuffled = [...productsData].sort(() => 0.5 - Math.random());
      const random = shuffled.slice(0, 6);
      setRandomProducts(random);

      setProducts(productsData);
      setCategories(categoriesData);
      setBanners(bannersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setProducts([]);
      setFeaturedProducts([]);
      setRandomProducts([]);
      setCategories([]);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Loading />
      </div>
    );
  }

  const displayBanners = banners.length > 0 ? banners : DEFAULT_BANNERS;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#fae127]/40 via-[#ef6728]/30 to-[#8eb2bb]/40 py-20 overflow-hidden" data-testid="hero-section">
        {/* Animated Sunburst Background */}
        <SunburstRays position="center" size="xlarge" speed="slow" opacity={0.45} />

        <div className="absolute top-10 left-10 w-20 h-20 text-[#FFD700] opacity-50 z-10">
          <Star className="w-full h-full animate-spin-slow" />
        </div>
        <div className="absolute bottom-20 right-10 w-16 h-16 text-[#FF6B00] opacity-50 z-10">
          <Sparkles className="w-full h-full animate-pulse" />
        </div>

        {/* Animated Astronaut */}
        <div className="absolute top-20 right-1/4 w-32 h-32 animate-astronaut z-10 pointer-events-none opacity-90 hidden lg:block">
          <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M256 16C167.6 16 96 87.6 96 176V224C96 230 96.6 235.8 97.7 241.4C106.8 288.6 148.6 324 198.4 324H313.6C363.4 324 405.2 288.6 414.3 241.4C415.4 235.8 416 230 416 224V176C416 87.6 344.4 16 256 16Z" fill="#E2E8F0"/>
            <path d="M256 48C185.3 48 128 105.3 128 176V208C128 208 170.7 240 256 240C341.3 240 384 208 384 208V176C384 105.3 326.7 48 256 48Z" fill="#1A1A2E"/>
            <path d="M304 144C304 170.5 282.5 192 256 192C229.5 192 208 170.5 208 144C208 117.5 229.5 96 256 96C282.5 96 304 117.5 304 144Z" fill="#00C2CB"/>
            <path d="M280 136C280 149.3 269.3 160 256 160C242.7 160 232 149.3 232 136C232 122.7 242.7 112 256 112C269.3 112 280 122.7 280 136Z" fill="#FFFFFF"/>
            <path d="M198.4 324H313.6C321.5 324 329.1 322.8 336.2 320.5V400C336.2 417.7 321.9 432 304.2 432H207.8C190.1 432 175.8 417.7 175.8 400V320.5C182.9 322.8 190.5 324 198.4 324Z" fill="#E2E8F0"/>
            <path d="M175.8 400V448C175.8 465.7 190.1 480 207.8 480H239.8V432H175.8V400Z" fill="#CBD5E1"/>
            <path d="M336.2 400V448C336.2 465.7 321.9 480 304.2 480H272.2V432H336.2V400Z" fill="#CBD5E1"/>
            <path d="M97.7 241.4C90 240 64 233.9 64 208C64 172.7 92.7 144 128 144V176C128 181.7 127 187.1 125.2 192C118.8 209.6 102.5 222.8 83.1 224L97.7 241.4Z" fill="#CBD5E1"/>
            <path d="M414.3 241.4C422 240 448 233.9 448 208C448 172.7 419.3 144 384 144V176C384 181.7 385 187.1 386.8 192C393.2 209.6 409.5 222.8 428.9 224L414.3 241.4Z" fill="#CBD5E1"/>
          </svg>
        </div>

        {/* Floating Confetti Toys */}
        <FloatingToys />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-fredoka font-bold text-[#1A1A2E] mb-6 tracking-wide" data-testid="hero-title">
                ¡Explora el universo de la diversión!
              </h1>
              <p className="text-lg sm:text-xl text-[#4A4A6A] font-quicksand mb-8 leading-relaxed">
                Desde 2007, llevamos alegría a tu hogar con los mejores juguetes para cada etapa del crecimiento.
              </p>
              <button
                onClick={() => navigate('/products')}
                className="bg-[#FF6B00] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#FF6B00]/90 hover:scale-105 transition-all duration-300 shadow-lg"
                data-testid="hero-cta-button"
              >
                Ver Productos
              </button>
            </div>
            <div className="relative w-full aspect-[4/3] rounded-3xl shadow-[0_0_80px_rgba(0,194,203,0.6)] overflow-hidden animate-float border-4 border-white/50">
              {displayBanners.map((banner, index) => (
                <img
                  key={banner.id}
                  src={banner.imageUrl}
                  alt={banner.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                    index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                />
              ))}
              
              {/* Carousel Indicators */}
              <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center space-x-2">
                {displayBanners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBanner(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentBanner ? 'bg-[#FFD700] w-6' : 'bg-white/60 hover:bg-white'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Carousel Section */}
      <BrandsCarousel />

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-4">
                Categorías
              </h2>
              <p className="text-lg text-[#4A4A6A] font-quicksand">
                Encuentra el juguete perfecto para cada edad
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {categories.map((category) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  onClick={() => navigate(`/products?category=${category.id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products Section */}
      {featuredProducts.length > 0 && (
        <section className="py-16 md:py-24" data-testid="featured-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-4">
                Productos Destacados
              </h2>
              <p className="text-lg text-[#4A4A6A] font-quicksand">
                Los favoritos de nuestros clientes
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Random Products Section */}
      {randomProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="random-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-4">
                Descubre Más Juguetes
              </h2>
              <p className="text-lg text-[#4A4A6A] font-quicksand">
                Una selección especial para ti
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {randomProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                onClick={() => navigate('/products')}
                className="bg-[#00C2CB] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#00C2CB]/90 hover:scale-105 transition-all duration-300 shadow-lg"
                data-testid="view-all-products-button"
              >
                Ver Todos los Productos
              </button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#00C2CB]/10 via-[#FFD700]/10 to-[#FF6B00]/10 overflow-hidden">
        {/* Animated Sunburst from top-right */}
        <SunburstRays position="top-right" size="large" speed="medium" opacity={0.35} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Rocket className="w-20 h-20 mx-auto mb-6 text-[#00C2CB]" />
          <h2 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-6">
            ¡Reserva tus juguetes favoritos!
          </h2>
          <p className="text-lg text-[#4A4A6A] font-quicksand mb-8">
            Agrégalos al carrito y complétanos tu información para reservarlos. Te contactaremos pronto.
          </p>
          <button
            onClick={() => navigate('/products')}
            className="bg-[#FF6B00] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#FF6B00]/90 hover:scale-105 transition-all duration-300 shadow-lg"
            data-testid="cta-explore-button"
          >
            Explorar Ahora
          </button>
        </div>
      </section>
    </div>
  );
};